import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from './icons';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Side sheet as a modal dialog: focus moves in and is trapped, Escape and the
 * backdrop close it, focus returns to the opener, the page behind is inert and
 * does not scroll.
 */
export default function Drawer({
  open,
  onClose,
  side = 'right',
  title,
  headerExtra,
  footer,
  children,
  initialFocusRef,
  className = '',
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const panel = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const root = document.getElementById('root');
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (root) root.inert = true;

    const focusTimer = window.setTimeout(() => {
      const target =
        (initialFocusRef && initialFocusRef.current) ||
        (panel.current && panel.current.querySelector(FOCUSABLE)) ||
        panel.current;
      if (target) target.focus();
    }, 0);

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel.current) return;
      const items = [...panel.current.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (root) root.inert = false;
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [open, initialFocusRef]);

  if (!open) return null;

  const position =
    side === 'right' ? 'right-0 motion-safe:animate-slide-in-right' : 'left-0 motion-safe:animate-slide-in-left';

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-[rgb(20_14_12/0.45)] motion-safe:animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`absolute inset-y-0 ${position} flex w-[min(100vw,27rem)] flex-col bg-background text-ink shadow-[0_0_60px_rgb(0_0_0/0.18)] outline-none ${className}`}
      >
        <div className="flex min-h-[64px] items-center justify-between gap-3 border-b border-border px-5">
          <h2 id={titleId} className="font-display text-xl">
            {title}
          </h2>
          <div className="flex items-center gap-1">
            {headerExtra}
            <button
              type="button"
              onClick={onClose}
              className="-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-full hover:bg-ink/5"
            >
              <CloseIcon className="h-5 w-5" />
              <span className="sr-only">{t('common.close')}</span>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain">{children}</div>
        {footer && <div className="border-t border-border bg-background px-5 py-4">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
